import { useTranslation } from 'react-i18next';
import type { ExtraBrandQuote } from '@/shared/lib/account-pricing';
import { formatMoney } from '@/shared/lib/intl';
import { withTaxNote } from '@/shared/lib/tax-note';

interface ExtraBrandQuotePanelProps {
  quote: ExtraBrandQuote;
  /** The headline, already translated (which brand, and where it goes). */
  title: string;
  /** Brands Agency includes. */
  included: number;
  /** Test id of the panel; the count line gets `${testId}-count`. */
  testId?: string;
}

/** The server's extra-brand price, the extras after this brand and the new total. */
export function ExtraBrandQuotePanel({ quote, title, included, testId = 'add-brand-extra' }: ExtraBrandQuotePanelProps): JSX.Element {
  const { t } = useTranslation();
  const money = (amount: number): string => withTaxNote(formatMoney(amount, quote.currency, { maximumFractionDigits: 0 }), quote.currency, t);
  return (
    <div role="status" data-testid={testId} className="rounded-[12px] border border-[var(--color-accent-border)] bg-[var(--color-accent-soft)] p-4">
      <p className="m-0 mb-2 font-semibold text-body text-[var(--ink)]">{title}</p>
      <p className="m-0 mb-2 text-ui text-[var(--ink-2)] leading-relaxed">
        {t(quote.interval === 'yearly' ? 'topbar.addBrandSheet.extraChargeYearly' : 'topbar.addBrandSheet.extraChargeMonthly', {
          included,
          price: money(quote.perBrand),
        })}
      </p>
      <p className="m-0 mb-1 text-ui text-[var(--ink-2)] tabular-nums" data-testid={`${testId}-count`}>
        {t('topbar.addBrandSheet.extraBrandsAfter', { count: quote.extrasAfter, price: money(quote.perBrand) })}
      </p>
      <p className="m-0 text-ui font-semibold text-[var(--ink)] tabular-nums">
        {t(quote.interval === 'yearly' ? 'topbar.addBrandSheet.newTotalYearly' : 'topbar.addBrandSheet.newTotalMonthly', {
          total: money(quote.total),
        })}
      </p>
    </div>
  );
}

/** Why no extra brand can be offered: the block message, translated. */
export function ExtraBrandBlockMessage({ block, testId }: { block: 'noQuote' | 'paidAgency'; testId: string }): JSX.Element {
  const { t } = useTranslation();
  return (
    <div role="status" data-testid={testId} className="rounded-[12px] border border-[var(--line)] bg-[var(--color-surface-2)] p-4">
      <div className="text-[24px] mb-1" aria-hidden="true">🏷️</div>
      <p className="m-0 mb-1 font-semibold text-body text-[var(--ink)]">{t(`topbar.addBrandSheet.block.${block}.title`)}</p>
      <p className="m-0 text-ui text-[var(--ink-2)] leading-relaxed">{t(`topbar.addBrandSheet.block.${block}.body`)}</p>
    </div>
  );
}
